import { useCallback, useRef } from 'react';
import { applyOptimisticMutation, commitMutation } from 'react-relay';
import type { Disposable, IEnvironment, MutationConfig, MutationParameters } from 'relay-runtime';

export type QueuedMutationCommitConfig<TMutation extends MutationParameters> = {
	generateDeferredVariables?: (oldVariables: TMutation['variables']) => TMutation['variables'];
};

/**
 * Returns a commit function to be provided to the `useMutation` hook that queues concurrent mutation requests to be
 * executed sequentially.
 *
 * Example usage:
 * ```
 * const queuedCommit = useQueuedMutationCommit();
 * const [commit] = useMutation(
 * 		graphql`
 * 			# Your GraphQL mutation
 * 		`,
 * 		queuedCommit,
 * 	);
 * ```
 */
export const useQueuedMutationCommit = <TMutation extends MutationParameters>({
	generateDeferredVariables,
}: QueuedMutationCommitConfig<TMutation>) => {
	const running = useRef(false);
	const queue = useRef<{ requeue: () => void; dispose: () => void }[]>([]);

	/**
	 * Dispose of any queued commits (in backwards order) and requeue each commit (which will execute the first commit
	 * immediately). We need to explicitly dispose and requeue commits so optimistic updates are performed in the
	 * correct order when the first queued commit is executed.
	 */
	const dequeue = useCallback(() => {
		running.current = false;
		const requeue = [];
		while (queue.current.length) {
			const entry = queue.current.pop();
			if (entry) {
				entry.dispose();
				requeue.unshift(entry.requeue);
			}
		}
		requeue.forEach((fn) => fn());
	}, []);

	const queuedCommit = useCallback(
		(environment: IEnvironment, config: MutationConfig<TMutation>): Disposable => {
			const disposables: Disposable[] = [];

			const requeue = () => {
				disposables.push(queuedCommit(environment, config));
			};

			const dispose = () => {
				while (disposables.length) {
					disposables.pop()?.dispose();
				}
			};

			const commit = () => {
				running.current = true;

				const disposable = commitMutation(environment, {
					...config,
					variables: generateDeferredVariables
						? generateDeferredVariables(config.variables)
						: config.variables,
					onCompleted: (response, errors) => {
						// Run any queued commits after the mutation has completed
						dequeue();
						config.onCompleted?.(response, errors);
					},
					onError: (error) => {
						// Run any queued commits after the mutation has failed
						dequeue();
						config.onError?.(error);
					},
				});
				disposables.push(disposable);
			};

			if (!running.current) {
				commit();
			} else {
				const optimisticDisposable = applyOptimisticMutation(environment, config);
				disposables.push(optimisticDisposable);
				queue.current.push({ requeue, dispose });
			}

			// Return a new disposable that cleans up the optimistic AND committed operations
			return {
				dispose,
			};
		},
		[dequeue, generateDeferredVariables],
	);
	return queuedCommit;
};
